import { useEffect, useState } from 'react'

import { useNavigate } from 'react-router-dom'

import { RoutingPaths } from 'app/routes/routes'

import { getDataForSearchTour } from 'pages/tours/search-result-page/libs/getDataForSearchTour'

import { selectionStore } from 'entities/tour/models'
import searchTourStore from 'entities/tour/models/search-tour/search-tour'
import tourList from 'entities/tour/models/tour-list/tour-list'

import { Hotel } from 'shared/api/tours/search'
import { HotelDetailApi } from 'shared/api/tours/tour-detail'

import { createSearchTourData } from '../libs'
import { shouldUseExistingHotelDetails } from '../libs/shouldUseExistingHotelDetails'

const loadHotelBySharingCode = async (sharingCode: string): Promise<Hotel> => {
  const { data: tourDetail } =
    await HotelDetailApi.getHotelDetailsBySharingCode(sharingCode)

  const hotel = tourDetail?.data

  if (!hotel) {
    throw new Error()
  }

  searchTourStore.setDataForSearchTour(createSearchTourData(hotel))

  const hotelSearchSessionId = hotel.request_params?.hotel_search_session_id

  if (hotelSearchSessionId) {
    tourList.setUid(hotelSearchSessionId)
  }

  return hotel
}

const loadHotelById = async (
  hotelId: string,
  hotelSearchSessionId: string,
): Promise<Hotel> => {
  const preparedData = getDataForSearchTour(
    searchTourStore.getDataForSearchTour(),
  )

  const { data: responseDetail } = await HotelDetailApi.getHotelDetails({
    ...preparedData,
    hotel_id: hotelId,
    uid: hotelSearchSessionId,
  })

  const hotel = responseDetail?.data

  if (!hotel) {
    throw new Error()
  }

  return hotel
}

const loadHotelRooms = async (hotel: Hotel): Promise<Hotel> => {
  if (!hotel.request_params) {
    return hotel
  }

  const { data: roomsResponse } = await HotelDetailApi.getHotelRooms({
    ...hotel.request_params,
    tour_filters: selectionStore.getRoomFilters(),
  })

  return {
    ...hotel,
    tours: roomsResponse?.data?.tours,
    tour_filter_data: roomsResponse?.data?.tour_filter_data,
  }
}

export const useHotelDetails = (
  hotelId?: string,
  sharingCode?: string,
  isViewMode?: boolean,
) => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [isRoomLoading, setIsRoomLoading] = useState(false)
  const [isError, setIsError] = useState(false)

  useEffect(() => {
    let isCancelled = false

    const loadHotel = async () => {
      try {
        const currentHotel = selectionStore.getSelectedHotel()
        const hotelSearchSessionId = tourList.getUid()

        if (!hotelId && !sharingCode) {
          navigate(RoutingPaths.tours.start())
          return
        }

        if (
          shouldUseExistingHotelDetails({
            isViewMode,
            sharingCode,
            currentHotelSharingCode: currentHotel?.sharing_code,
          })
        ) {
          setIsLoading(false)
          setIsRoomLoading(false)
          setIsError(false)
          return
        }

        setIsLoading(true)
        setIsRoomLoading(false)
        setIsError(false)
        selectionStore.setSelectedHotel(null)

        const hotel =
          hotelId && hotelSearchSessionId
            ? await loadHotelById(hotelId, hotelSearchSessionId)
            : sharingCode
              ? await loadHotelBySharingCode(sharingCode)
              : null

        if (!hotel) {
          setIsError(true)
          return
        }

        if (isCancelled) return

        selectionStore.setSelectedHotel(hotel)
        setIsLoading(false)

        if (!hotel.request_params) return

        setIsRoomLoading(true)

        try {
          const hotelWithRooms = await loadHotelRooms(hotel)

          if (!isCancelled) {
            selectionStore.setSelectedHotel(hotelWithRooms)
          }
        } catch {
        } finally {
          if (!isCancelled) {
            setIsRoomLoading(false)
          }
        }
      } catch {
        if (!isCancelled) {
          setIsError(true)
        }
      } finally {
        if (!isCancelled && !selectionStore.getSelectedHotel()) {
          setIsLoading(false)
        }
      }
    }

    void loadHotel()

    return () => {
      isCancelled = true
    }
  }, [hotelId, sharingCode, isViewMode, navigate])

  return { isLoading, isError, isRoomLoading }
}
